/**
 * รายการของฉัน
 *
 * รวมสองแหล่งไว้ในหน้าเดียว: คิวในเครื่องที่ยังส่งไม่สำเร็จ กับรายการที่อยู่บนเซิร์ฟเวอร์แล้ว
 * คิวขึ้นก่อนเสมอ เพราะเป็นสิ่งที่ผู้ใช้ต้องจัดการ ส่วนรายการที่สำเร็จแล้วแค่ไว้ตรวจย้อนหลัง
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useQueue } from '../hooks/useQueue';
import { QUEUE_STATUS_LABELS_TH } from '../lib/db';
import type { CaptureView } from '../../shared/types';
import { formatDateTimeTh } from '../../shared/time';
import { metres, statusLabel, statusPillClass } from '../lib/format';
import { PAGE_SIZE_DEFAULT } from '../../shared/config';
import { ConfirmDialog } from '../components/ConfirmDialog';
import type { QueuedCapture } from '../lib/db';

export function MyListRoute({ userId }: { userId: string }) {
  const { items: queued, counts, retry, discard } = useQueue();
  /** รายการในคิวที่กำลังจะถูกทิ้ง null = ไม่มีกล่องยืนยันเปิดอยู่ */
  const [discarding, setDiscarding] = useState<QueuedCapture | null>(null);
  const [discardBusy, setDiscardBusy] = useState(false);
  const [captures, setCaptures] = useState<CaptureView[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    // ไม่ตั้ง loading ตรงนี้ ค่าเริ่มต้นเป็น true อยู่แล้ว และปุ่ม "โหลดเพิ่ม" ตั้งเองก่อนเรียก
    // การ setState แบบ synchronous ใน effect ทำให้เกิด render รอบที่เสียเปล่า
    async (nextCursor?: string) => {
      try {
        const params = new URLSearchParams({
          createdBy: userId,
          limit: String(PAGE_SIZE_DEFAULT),
        });
        /*
         * ไม่แสดงรายการที่ลบไปแล้ว
         *
         * ตัวกรองรับเฉพาะสถานะที่ "เอา" ไม่ใช่สถานะที่ "ไม่เอา" จึงต้องไล่ระบุเอง
         * (คั่นด้วยจุลภาคในพารามิเตอร์เดียว ตามที่ parseList ฝั่ง server อ่าน)
         * ถ้าไม่กรอง รายการที่เจ้าหน้าที่เพิ่งลบจะยังโผล่อยู่ในหน้านี้ด้วยป้าย "ยกเลิก"
         * ซึ่งดูเหมือนลบไม่สำเร็จ
         *
         * ข้อมูลยังอยู่ในระบบและหัวหน้างานยังค้นเจอได้จากหน้าค้นหา — ซ่อนเฉพาะที่นี่
         */
        params.set('status', 'draft,uploading,completed,failed');
        if (nextCursor) params.set('cursor', nextCursor);

        const page = await api.listCaptures(params.toString());
        setCaptures((current) => (nextCursor ? [...current, ...page.items] : page.items));
        setCursor(page.nextCursor);
      } catch (caught) {
        setError(
          caught instanceof ApiError
            ? caught.message
            : 'โหลดรายการไม่สำเร็จ ตรวจสอบการเชื่อมต่อแล้วลองใหม่',
        );
      } finally {
        setLoading(false);
      }
    },
    [userId],
  );

  useEffect(() => {
    /*
     * eslint-disable-next-line react-hooks/set-state-in-effect --
     * นี่คือการโหลดข้อมูลตอน mount ซึ่งจำเป็นต้องอยู่ใน effect
     * setState ทุกจุดใน load() เกิดหลัง await แล้วทั้งหมด ไม่ใช่ synchronous
     * แต่ rule ตรวจแบบ transitive จึงมองไม่เห็นความต่างนี้
     * มี AbortController ยกเลิก request เมื่อ component ถูก unmount แล้ว
     */
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load();
  }, [load]);

  const pending = queued.filter((item) => item.status !== 'sent');

  return (
    <div className="wrap stack-lg">
      <div className="spread">
        <h1>รายการของฉัน</h1>
        <Link to="/capture" className="btn btn-secondary">
          บันทึกใหม่
        </Link>
      </div>

      {pending.length > 0 && (
        <section className="stack">
          <div className="spread">
            <h2>คิวในเครื่อง ({pending.length})</h2>
            {counts.failed > 0 && (
              <button type="button" className="btn btn-secondary" onClick={() => retry()}>
                ส่งใหม่ทั้งหมด
              </button>
            )}
          </div>

          <div className="list">
            {pending.map((item) => (
              /*
                แถวนี้ไม่ใช่ Link ทั้งแถวเหมือนรายการที่ส่งแล้ว
                เพราะต้องมีปุ่มทิ้งอยู่ในแถวด้วย และปุ่มซ้อนในลิงก์ทำให้กดพลาดง่ายมาก
                จึงให้เฉพาะส่วนข้อความเป็นลิงก์ ปุ่มแยกออกมาต่างหาก
              */
              <div key={item.id} className="list-row">
                <Link to={`/result/${item.id}`} className="grow">
                  <b>{item.customerRegistration}</b>
                  <p>
                    {formatDateTimeTh(item.createdAt)} · ±{metres(item.gps.accuracyM)}
                    {item.attempts > 0 ? ` · ลองแล้ว ${item.attempts} ครั้ง` : ''}
                  </p>
                </Link>
                <span
                  className={
                    item.status === 'failed'
                      ? 'pill pill-bad'
                      : item.status === 'sending'
                        ? 'pill pill-info'
                        : 'pill pill-warn'
                  }
                >
                  {QUEUE_STATUS_LABELS_TH[item.status]}
                </span>
                {/* ทิ้งได้เฉพาะรายการที่ไม่ได้กำลังส่งอยู่ ไม่งั้นอาจถึงเซิร์ฟเวอร์แล้ว */}
                {item.status !== 'sending' && (
                  <button
                    type="button"
                    className="btn btn-quiet"
                    aria-label={`ทิ้งรายการ ${item.customerRegistration} ออกจากคิว`}
                    onClick={() => setDiscarding(item)}
                  >
                    ทิ้ง
                  </button>
                )}
              </div>
            ))}
          </div>

          <p className="hint">
            รายการเหล่านี้เก็บอยู่ในเครื่อง อย่าล้างข้อมูลเบราว์เซอร์จนกว่าจะส่งสำเร็จทั้งหมด
          </p>
        </section>
      )}

      <section className="stack">
        <h2>รายการที่ส่งแล้ว</h2>

        {error && (
          <div className="banner banner-bad" role="alert">
            <div className="grow">{error}</div>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                setLoading(true);
                void load();
              }}
            >
              ลองใหม่
            </button>
          </div>
        )}

        {captures.length === 0 && !loading && !error && (
          <div className="empty">ยังไม่มีรายการที่ส่งสำเร็จ</div>
        )}

        {captures.length > 0 && (
          <div className="list">
            {captures.map((capture) => (
              <Link key={capture.id} to={`/captures/${capture.id}`} className="list-row">
                <div className="grow">
                  <b>{capture.customerRegistrationNormalized}</b>
                  <p>
                    {formatDateTimeTh(capture.capturedAt)} · ±{metres(capture.accuracyM)}
                    {capture.gpsOverride !== 'none' ? ' · override' : ''}
                  </p>
                </div>
                <span className={statusPillClass(capture.status)}>
                  {capture.gpsOverride === 'requested' ? 'รออนุมัติ' : statusLabel(capture.status)}
                </span>
              </Link>
            ))}
          </div>
        )}

        {loading && (
          <div className="empty">
            <span className="spinner" style={{ margin: '0 auto' }} />
          </div>
        )}

        {cursor && !loading && (
          <button
            type="button"
            className="btn btn-secondary btn-block"
            onClick={() => {
              setLoading(true);
              void load(cursor);
            }}
          >
            โหลดเพิ่ม
          </button>
        )}
      </section>

      <ConfirmDialog
        open={discarding !== null}
        title="ทิ้งรายการนี้ออกจากคิว"
        confirmLabel="ยืนยันทิ้ง"
        busy={discardBusy}
        onCancel={() => setDiscarding(null)}
        onConfirm={() => {
          if (!discarding) return;
          setDiscardBusy(true);
          void discard(discarding.id).finally(() => {
            setDiscardBusy(false);
            setDiscarding(null);
          });
        }}
      >
        <div className="banner banner-bad">
          <span>
            รายการนี้ยังไม่ถูกส่งขึ้นระบบ ข้อมูลและรูปอยู่ในเครื่องนี้ที่เดียว ทิ้งแล้วกู้กลับไม่ได้
            ต้องออกไปถ่ายใหม่
          </span>
        </div>
        {discarding && (
          <dl className="kv">
            <div>
              <dt>เลขทะเบียน</dt>
              <dd>{discarding.customerRegistration}</dd>
            </div>
            <div>
              <dt>เวลาที่บันทึก</dt>
              <dd>{formatDateTimeTh(discarding.createdAt)}</dd>
            </div>
            <div>
              <dt>พยายามส่งแล้ว</dt>
              <dd>{discarding.attempts} ครั้ง</dd>
            </div>
          </dl>
        )}
        <p className="small muted">
          ถ้ายังไม่แน่ใจ ให้กด &quot;ส่งใหม่ทั้งหมด&quot; ดูก่อน อาจแค่สัญญาณไม่ดีตอนนั้น
        </p>
      </ConfirmDialog>
    </div>
  );
}
